import {
  GetAllArticlesParameters,
  GetArticleByIdParameters,
} from 'core_main/sdk/get_articles/types';
import { getMappedLanguage } from 'i18n/helpers';
import {
  ARTICLES_SEARCH_ENDPOINT,
  ARTICLE_BY_ID_ENDPOINT,
  KNOWLEDGE_BASE_ARTICLE_FEEDBACK_ENDPOINT,
  KNOWLEDGE_BASE_ARTICLE_RATING_ENDPOINT,
  FEATURED_KNOWLEDGE_BASE_ARTICLES,
  ALL_ARTICLES_ENDPOINT,
} from './constants';
import { ArticlesSearchParameters } from './types';

export const validateArticlesSearchParameters = (
  parameters: ArticlesSearchParameters,
) => {
  const term = parameters?.term;
  const lang = parameters?.lang;

  if (!term || !lang) {
    return new Error(
      'Missing one or more of the required properties: lang, term',
    );
  }

  return undefined;
};

export const validateArticleByIdParameters = (
  parameters: GetArticleByIdParameters,
) => {
  const articleId = parameters?.articleId;
  const lang = parameters?.lang;

  if (!articleId || !lang) {
    return new Error(
      'Missing one or more of the required properties: articleId, lang',
    );
  }

  return undefined;
};

export const makeArticlesSearchEndpoint = (
  parameters: ArticlesSearchParameters,
) => {
  const { term, lang, knowledgeBaseId } = parameters;

  const mappedLang = getMappedLanguage(lang);

  let endpoint = `/p/v4/kb/articles/search?pageSize=100&page=1&term=${term}&knowledgeBaseId=${knowledgeBaseId}`;

  if (mappedLang) {
    endpoint = `${endpoint}&lang=${mappedLang}`;
  }

  return endpoint as typeof ARTICLES_SEARCH_ENDPOINT;
};

export const makeArticleByIdEndpoint = (
  parameters: GetArticleByIdParameters,
) => {
  const { articleId, lang, knowledgeBaseId } = parameters;
  const mappedLang = getMappedLanguage(lang);

  let url = ARTICLE_BY_ID_ENDPOINT.replace(':articleId', articleId);

  const queryStrings = [] as string[];

  if (mappedLang) {
    queryStrings.push(`lang=${mappedLang}`);
  }

  if (knowledgeBaseId) {
    queryStrings.push(`knowledgeBaseId=${knowledgeBaseId}`);
  }

  if (queryStrings.length) {
    url = `${url}?${queryStrings.join('&')}`;
  }

  return url as typeof ARTICLE_BY_ID_ENDPOINT;
};

export const makeAllArticlesEndpoint = (
  parameters: GetAllArticlesParameters,
) => {
  const { lang, knowledgeBaseId, page } = parameters;
  const mappedLang = getMappedLanguage(lang);

  let url = ALL_ARTICLES_ENDPOINT;

  const queryStrings = [] as string[];

  if (mappedLang) {
    queryStrings.push(`lang=${mappedLang}`);
  }

  if (knowledgeBaseId) {
    queryStrings.push(`knowledgeBaseId=${knowledgeBaseId}`);
  }

  if (page) {
    queryStrings.push(`page=${page}`);
  }

  if (queryStrings.length) {
    url = `${url}?${queryStrings.join('&')}`;
  }

  return url as typeof ALL_ARTICLES_ENDPOINT;
};

export const makePostArticleRatingEndpoint = (
  articleId: string,
  lang?: string,
) => {
  const mappedLang = getMappedLanguage(lang);
  return `/p/v4/kb/articles/${articleId}/feedback?lang=${mappedLang}` as typeof KNOWLEDGE_BASE_ARTICLE_RATING_ENDPOINT;
};

export const makePatchArticleFeedbackEndpoint = (
  articleId: string,
  id: string,
) => {
  return `/p/v4/kb/articles/${articleId}/feedback/${id}` as typeof KNOWLEDGE_BASE_ARTICLE_FEEDBACK_ENDPOINT;
};

export const makeFetchFeatureArticlesEnpoint = (
  knowledgeBaseId: string,
  lang?: string,
) => {
  const mappedLang = getMappedLanguage(lang);
  return `/p/v4/kb/featured-articles?knowledgeBaseId=${knowledgeBaseId}&lang=${mappedLang}` as typeof FEATURED_KNOWLEDGE_BASE_ARTICLES;
};
